/**
 * Generative UI payload types.
 *
 * These mirror the JSON schema defined in backend/internal/tools/present_data.go.
 * The LLM calls the `present_data` tool with a payload matching one of these
 * shapes; the HTTP layer converts the tool_result into a `gen_ui` SSE event
 * whose `gen_ui` field is the raw payload.
 */

export interface MetricCard {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}

export interface MetricCards {
  type: "metric_cards";
  title?: string;
  cards: MetricCard[];
}

export interface BarChartRow {
  label: string;
  value: number;
}

export interface BarChart {
  type: "bar_chart";
  title?: string;
  x_label?: string;
  y_label?: string;
  data: BarChartRow[];
}

export interface LineChartRow {
  label: string;
  value: number;
}

export interface LineChartSeries {
  name: string;
  data: LineChartRow[];
}

export interface LineChart {
  type: "line_chart";
  title?: string;
  x_label?: string;
  y_label?: string;
  data?: LineChartRow[];
  series?: LineChartSeries[];
}

export interface ComparisonTable {
  type: "comparison_table";
  title?: string;
  columns: string[];
  rows: string[][];
}

export interface PropertyMetric {
  label: string;
  value: string;
}

export interface PropertySummary {
  type: "property_summary";
  address: string;
  town?: string;
  flat_type?: string;
  floor_area?: string;
  price: string;
  psf?: string;
  lease?: string;
  metrics?: PropertyMetric[];
}

export interface ReviewSnippetCard {
  url: string;
  title: string;
  snippet: string;
  source: string;          // "reddit.com/r/singapore", "stackedhomes.com", …
  backend: string;         // "reddit" | "brave" — drives subtle styling cues
  published_at?: string;   // ISO-8601 when known
  subreddit?: string;
  num_comments?: number;
  score?: number;
}

export interface ReviewSnippets {
  type: "review_snippets";
  title?: string;
  project?: string;
  query?: string;
  cards: ReviewSnippetCard[];
}

export type GenUIPayload =
  | MetricCards
  | BarChart
  | LineChart
  | ComparisonTable
  | PropertySummary
  | ReviewSnippets;

/** Type-guard: checks the payload has a known type field. */
export function isValidGenUI(p: unknown): p is GenUIPayload {
  if (!p || typeof p !== "object") return false;
  const t = (p as { type?: unknown }).type;
  return (
    t === "metric_cards" ||
    t === "bar_chart" ||
    t === "line_chart" ||
    t === "comparison_table" ||
    t === "property_summary" ||
    t === "review_snippets"
  );
}
